// Encrypted file sharing wire + crypto (F-COM-5). The file is AES-GCM
// encrypted client-side under a per-file key HKDF'd from the room secret; only
// ciphertext reaches S3. The reference (object key, name, size, nonce) is
// sealed like every data frame (sealed.ts, SPEC §5.2a), so the filename and
// friends cross the SFU as ciphertext too.

export const FILE_TOPIC = 'bz.file';

/** Ciphertext size cap (F-COM-5: 100MB). Checked client-side before upload. */
export const MaxFileSize = 100 << 20;

export interface FileRef {
  v: 1;
  fileId: string;
  objectKey: string;
  name: string;
  size: number;
  mime: string;
  nonceB64: string; // AES-GCM IV
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function encodeFileRef(ref: FileRef): Uint8Array<ArrayBuffer> {
  return new Uint8Array(encoder.encode(JSON.stringify(ref)));
}

export function decodeFileRef(payload: Uint8Array): FileRef | null {
  try {
    const r = JSON.parse(decoder.decode(payload)) as FileRef;
    if (r.v !== 1 || !r.fileId || !r.objectKey || !r.nonceB64) return null;
    if (typeof r.name !== 'string' || typeof r.size !== 'number') return null;
    return r;
  } catch {
    return null;
  }
}

/** Encrypt a file. Returns ciphertext + the GCM nonce. */
export async function encryptFile(
  key: CryptoKey,
  data: ArrayBuffer,
): Promise<{ ciphertext: ArrayBuffer; nonce: Uint8Array<ArrayBuffer> }> {
  const nonce = new Uint8Array(new ArrayBuffer(12));
  crypto.getRandomValues(nonce);
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, key, data);
  return { ciphertext, nonce };
}

export async function decryptFile(
  key: CryptoKey,
  nonce: Uint8Array<ArrayBuffer>,
  ciphertext: ArrayBuffer,
): Promise<ArrayBuffer> {
  return crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce }, key, ciphertext);
}

export function bytesToBase64(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

export function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function randomFileId(): string {
  const b = new Uint8Array(12);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}
